// Split-dropdown for query-plan inspection. The button opens a small menu:
//   • Explain                — EXPLAIN <stmt>  (tabular; works on every version
//                              incl. MySQL 5.0)
//   • Explain (JSON tree)    — EXPLAIN FORMAT=JSON <stmt>  (MySQL 5.6+ /
//                              MariaDB 10.1+) → rendered as a cost/rows tree
//                              with cost-weighted heatmap (hottest read_cost
//                              tinted hot; 🔥 marker on the single hottest node).
//   • Plan + Actual          — EXPLAIN FORMAT=JSON followed by EXPLAIN ANALYZE
//                              FORMAT=JSON (MariaDB: ANALYZE FORMAT=JSON), then
//                              merges the actual_rows / actual_time onto the
//                              plan tree as overlay badges. Misestimates
//                              (estimate vs actual off by ≥2×) tint warning.
//                              ⚠ EXECUTES the query (it's an ANALYZE).
//   • Explain Analyze        — EXPLAIN ANALYZE <stmt> (tree-format text only).
//                              Kept for compatibility with older MySQL 8 versions
//                              that don't support ANALYZE FORMAT=JSON yet.
// All operate on the statement under the cursor (same selection rule as Run).
// Unsupported variants just surface MySQL's error in the message log.
// Reuses ExportMenu's dropdown styling (like FormatMenu).
//
// Props:
//   onPick(mode)  fn — 'plan' | 'json' | 'analyze' | 'plan+actual'
//   disabled      bool
//   serverInfo    object — { display, flavour, capabilities: {explainJson,
//                  explainAnalyze, explainAnalyzeJson} } from the session-
//                  create response. When present, items the server can't run
//                  are greyed out with a tooltip naming the version requirement.
//                  When null/undefined (e.g. detection blip) ALL items are
//                  enabled — preferring false-positive over false-negative
//                  so a transient failure doesn't hide working features.
import { createSignal, Show } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';

export default function ExplainMenu(props) {
  const [open, setOpen] = createSignal(false);
  let btnRef, menuRef;
  useDismissable(() => [btnRef, menuRef], () => setOpen(false), { event: 'click' });

  const pick = (mode) => {
    if (modeDisabled(mode)) return;
    setOpen(false);
    props.onPick?.(mode);
  };

  // capability lookup with safe defaults (= "supported" when we don't know).
  const caps = () => props.serverInfo?.capabilities || {
    explainJson: true, explainAnalyze: true, explainAnalyzeJson: true,
  };
  const flavour = () => props.serverInfo?.flavour || '';
  const display = () => props.serverInfo?.display || '';

  // mode → reason the item is disabled (empty string when enabled). The reason
  // is the tooltip on the disabled item so the user knows *why* it's grey.
  const reasonFor = (mode) => {
    const c = caps();
    if (mode === 'json' && !c.explainJson) {
      return `${display() || 'this server'} doesn't support EXPLAIN FORMAT=JSON — needs ` +
             (flavour() === 'mariadb' ? 'MariaDB 10.1+' : 'MySQL 5.6+') + '.';
    }
    if (mode === 'analyze' && !c.explainAnalyze) {
      return `${display() || 'this server'} doesn't support EXPLAIN ANALYZE — needs ` +
             (flavour() === 'mariadb' ? 'MariaDB 10.1+' : 'MySQL 8.0.18+') + '.';
    }
    if (mode === 'plan+actual' && (!c.explainJson || !c.explainAnalyzeJson)) {
      return `${display() || 'this server'} doesn't support both EXPLAIN FORMAT=JSON AND ANALYZE FORMAT=JSON — Plan+Actual needs ` +
             (flavour() === 'mariadb' ? 'MariaDB 10.1+' : 'MySQL 8.0.32+') + '.';
    }
    return '';
  };
  const modeDisabled = (mode) => !!reasonFor(mode);
  // Compose the base tooltip + the "(disabled because …)" suffix for grey items.
  const tipFor = (mode, base) => {
    const r = reasonFor(mode);
    return r ? `${base}\n\n⚠ Disabled: ${r}` : base;
  };

  return (
    <div class="export-menu">
      <button ref={btnRef} class="btn btn-sm btn-ghost" disabled={props.disabled}
              onClick={() => setOpen(!open())}
              title={`Show the query plan for the statement under the cursor (EXPLAIN)${display() ? ` · ${display()}` : ''}`}>
        ▤ Explain ▾
      </button>
      <Show when={open()}>
        <div ref={menuRef} class="export-menu-pop">
          <button class="export-menu-item" onClick={() => pick('plan')}
                  title="EXPLAIN — the plan as a table. Works on every MySQL/MariaDB version.">
            Explain (tabular)
          </button>
          <button class="export-menu-item" disabled={modeDisabled('json')}
                  onClick={() => pick('json')}
                  title={tipFor('json', 'EXPLAIN FORMAT=JSON — the plan as a cost/rows tree (heatmap-tinted by per-node cost; hottest node marked 🔥). MySQL 5.6+ / MariaDB 10.1+.')}>
            Explain (JSON tree)
          </button>
          <button class="export-menu-item" disabled={modeDisabled('plan+actual')}
                  onClick={() => pick('plan+actual')}
                  title={tipFor('plan+actual', 'Plan + Actual — runs EXPLAIN FORMAT=JSON then ANALYZE FORMAT=JSON, merges actual_rows / actual_time onto the tree, and tints misestimates (estimate vs actual off by ≥2×). ⚠ EXECUTES the query.')}>
            Plan + Actual ⚡ ⚠ runs it
          </button>
          <button class="export-menu-item" disabled={modeDisabled('analyze')}
                  onClick={() => pick('analyze')}
                  title={tipFor('analyze', 'EXPLAIN ANALYZE — tree-format text output. Use this on MySQL 8.0.18-8.0.31 where ANALYZE FORMAT=JSON isn\'t supported yet. ⚠ EXECUTES the query.')}>
            Explain Analyze (text) ⚠ runs it
          </button>
          <div class="export-menu-sep" />
          <button class="export-menu-item" onClick={() => pick('profile')}
                  title="Query Profiler — per-stage time breakdown via session profiling (SET profiling / SHOW PROFILE) plus the SHOW STATUS counter delta. Works on MariaDB 10.0+. ⚠ EXECUTES the query.">
            Profile ⏱ ⚠ runs it
          </button>
          <Show when={display()}>
            <div class="export-menu-sep" />
            <div class="export-menu-footer muted">Server: <span class="mono">{display()}</span></div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
